/**
 * Carousel behaviour, shared by every .carousel track on the page.
 *
 * The track already scrolls natively: it is overflow-x: auto, so a trackpad,
 * a touch screen and the arrow keys all work with no JavaScript. What this
 * adds is what those leave out on a desktop with a plain mouse, which has no
 * horizontal wheel and no way to drag.
 *
 *   - prev / next buttons that move by whole cards
 *   - click and drag on the track itself
 *   - buttons disable themselves at each end
 *
 * Everything degrades: if this file fails to load the track still scrolls,
 * and the buttons are rendered disabled until it runs.
 */
(function () {
  'use strict';

  function step(track) {
    var first = track.firstElementChild;
    if (!first) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function syncButtons(track, prev, next) {
    var max = track.scrollWidth - track.clientWidth;
    var x = track.scrollLeft;
    if (prev) prev.disabled = x <= 1;
    if (next) next.disabled = x >= max - 1;
  }

  function init(root) {
    var track = root.querySelector('.carousel');
    if (!track || track.dataset.carouselReady) return;
    track.dataset.carouselReady = 'true';

    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');

    /* Track the destination rather than reading scrollLeft each time.
       A smooth scroll is still animating when the next click arrives, so
       scrollBy would measure a position mid-flight and six quick clicks
       would advance two cards instead of six. */
    var target = null;

    function nudge(direction) {
      var max = track.scrollWidth - track.clientWidth;
      var from = target === null ? track.scrollLeft : target;
      target = Math.max(0, Math.min(max, from + direction * step(track)));
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    /* Any scroll the user drives themselves invalidates the destination. */
    var settle;
    track.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () { target = null; }, 120);
    }, { passive: true });

    track.addEventListener('scroll', function () {
      syncButtons(track, prev, next);
    }, { passive: true });
    window.addEventListener('resize', function () {
      syncButtons(track, prev, next);
    });
    syncButtons(track, prev, next);

    /* Drag to scroll. Only for a real mouse: touch already pans natively,
       and hijacking it there would fight the browser's own momentum. */
    var down = false;
    var startX = 0;
    var startScroll = 0;
    var moved = 0;
    var suppressClick = false;

    /* Images and links are draggable by default. Without this the browser
       starts its own drag-and-drop the moment the pointer moves on a card,
       which cancels our pointer events: the track then only responded to
       the arrow buttons. */
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    /* Deliberately no preventDefault here: on a link, suppressing the
       default action on pointerdown also suppresses the click that follows,
       so a plain click on a card stopped opening the product. Cancelling
       dragstart above is enough to stop the browser's native drag. */
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = 0;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.cursor = 'grabbing';
    });

    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (moved > 3) {
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
        track.style.userSelect = 'none';
        e.preventDefault();
      }
      track.scrollLeft = startScroll - dx;
    });

    function endDrag(e) {
      if (!down) return;
      down = false;
      /* Arm the click guard here, and disarm it on a timer. Keying off a
         lingering "moved" value meant that when a drag ended without the
         browser firing a click, the stale value swallowed the *next*
         genuine click and the card stopped opening. */
      suppressClick = moved > 3;
      setTimeout(function () { suppressClick = false; }, 100);
      moved = 0;
      track.style.scrollBehavior = '';
      track.style.cursor = '';
      track.style.userSelect = '';
      if (track.hasPointerCapture && e.pointerId !== undefined) {
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
      }
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    /* A drag that ends on a card must not also follow its link. */
    track.addEventListener('click', function (e) {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  function initAll() {
    document.querySelectorAll('[data-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
